import React, { useMemo } from 'react';
import './PrintDocument.css';
import ShopsHelper from 'helpers/ShopsHelper';
import { PRINT_TYPES } from 'const';
import { QRCodeSVG } from 'qrcode.react';
import { getUpiPaymentDetails } from 'helpers/SaleHelper';

const ThreeInchInvoice = ({ data, shop, billFormat, type = PRINT_TYPES.SALE, totals, terminal }) => {
  const items = useMemo(() => data.items || [], [data.items]);
  const dateValue = data.time || data.created_at;
  const date = dateValue ? new Date(dateValue) : new Date();
  const options = dateValue ? { timeZone: 'UTC' } : {};

  const formattedDate = date.toLocaleDateString('en-GB', options);
  const formattedTime = date
    .toLocaleTimeString('en-GB', { ...options, hour: '2-digit', minute: '2-digit', hour12: true })
    .toUpperCase();

  const customer = data.customer || null;
  const shopName = billFormat?.shop_name || shop?.shop_name;
  const documentTitle = type === PRINT_TYPES.ESTIMATE ? 'ESTIMATE' : billFormat?.header || 'TAX INVOICE';
  const grandTotal = items.reduce((sum, item) => sum + (item.payable || 0), 0);
  const totalTaxVal = useMemo(() => items.reduce((sum, item) => sum + (Number(item.tax) || 0), 0), [items]);
  const totalCessVal = useMemo(() => items.reduce((sum, item) => sum + (Number(item.cess) || 0), 0), [items]);
  const { upiId, upiPaymentUrl } = getUpiPaymentDetails({
    terminal,
    amount: totals?.payable,
    description: data.bill_no || data.estimate_no,
    shop,
  });

  return (
    <div
      className="thermal-receipt print-only"
      style={{
        width: '76.2mm',
        margin: '0',
        padding: '10px',
        fontFamily: 'monospace',
        fontSize: '13px',
        color: '#000',
        lineHeight: '1.2',
      }}
    >
      <style type="text/css" media="print">
        {`
                    @page { size: 76.2mm auto; margin: 0 !important; }
                    .thermal-receipt.print-only {
                        width: 76.2mm !important;
                    }
                `}
      </style>

      <div style={{ textAlign: 'center', marginBottom: '10px' }}>
        <h2 style={{ margin: '0 0 5px 0', fontSize: '18px' }}>{shopName}</h2>
        {billFormat?.address_1 && <div>{billFormat.address_1}</div>}
        {billFormat?.address_2 && <div>{billFormat.address_2}</div>}
        {billFormat?.address_3 && <div>{billFormat.address_3}</div>}
        {billFormat?.address_4 && <div>{billFormat.address_4}</div>}
        {billFormat?.tel_no && <div>{billFormat.tel_no}</div>}
        {billFormat?.tin && <div>{billFormat.tin}</div>}
      </div>
      <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '16px', margin: '5px 0' }}>{documentTitle}</div>

      <div style={{ borderBottom: '1px dashed black', margin: '5px 0' }}></div>

      <div style={{ margin: '5px 0' }}>
        <div>Bill no: {data.bill_no}</div>
        <div>Date: {formattedDate}</div>
        <div>Time: {formattedTime}</div>
        {customer && (
          <>
            <div>Customer: {customer.name}</div>
            <div>Mobile: {customer.mobile}</div>
            {customer.points && <div>Loyalty points: {customer.points}</div>}
          </>
        )}
      </div>

      <div style={{ borderBottom: '1px dashed black', margin: '5px 0' }}></div>

      <div style={{ display: 'flex', fontWeight: 'bold', margin: '5px 0' }}>
        <span style={{ width: '10%' }}>#</span>
        <span style={{ width: '40%' }}>Item</span>
        <span style={{ width: '20%', textAlign: 'right' }}>Price</span>
        <span style={{ width: '10%', textAlign: 'right' }}>Qty</span>
        <span style={{ width: '20%', textAlign: 'right' }}>Total</span>
      </div>

      <div style={{ borderBottom: '1px dashed black', margin: '5px 0' }}></div>

      <div>
        {items.map((item, index) => (
          <div key={index} style={{ display: 'flex', flexDirection: 'column', marginBottom: '8px' }}>
            <div style={{ display: 'flex' }}>
              <span style={{ width: '10%' }}>{index + 1}</span>
              <span style={{ width: '90%' }}>{item.name || item.item?.name || item.description}</span>
            </div>
            <div style={{ display: 'flex' }}>
              <span style={{ width: '50%' }}></span>
              <span style={{ width: '20%', textAlign: 'right' }}>
                {ShopsHelper.getAmountFormatted(shop, parseFloat(item.rate || 0))}
              </span>
              <span style={{ width: '10%', textAlign: 'right' }}>
                {ShopsHelper.getAmountFormatted(shop, item.quantity || 0)}
              </span>
              <span style={{ width: '20%', textAlign: 'right' }}>
                {ShopsHelper.getAmountFormatted(shop, parseFloat(item.payable || 0))}
              </span>
            </div>
          </div>
        ))}

        {(data.discount_applied_on_items || 0) + (data.discount || 0) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '5px 0' }}>
            <span style={{ paddingLeft: '30%' }}>Discount :</span>
            <span>
              {ShopsHelper.getAmountFormatted(shop, (data.discount_applied_on_items || 0) + (data.discount || 0))}
            </span>
          </div>
        )}
      </div>

      {totalTaxVal + totalCessVal > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '5px 0' }}>
          <span style={{ paddingLeft: '30%' }}>Total Tax :</span>
          <span>{ShopsHelper.getAmountFormatted(shop, totalTaxVal + totalCessVal)}</span>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '15px',
          fontWeight: 'bold',
          margin: '10px 0',
        }}
      >
        <span style={{ paddingLeft: '30%' }}>Total :</span>
        <span>{ShopsHelper.getAmountFormatted(shop, parseFloat(grandTotal))}</span>
      </div>
      {upiPaymentUrl && (
        <>
          <div style={{ borderBottom: '1px dashed black', margin: '10px 0' }} />
          <div style={{ textAlign: 'center', margin: '10px 0' }}>
            <QRCodeSVG value={upiPaymentUrl} size={110} includeMargin />
            <div style={{ marginTop: '5px', fontWeight: 'bold', fontSize: '12px' }}>Scan & Pay</div>
            <div style={{ fontSize: '10px', wordBreak: 'break-all' }}>{upiId}</div>
          </div>
        </>
      )}
      <div style={{ textAlign: 'center', fontSize: '14px', margin: '15px 0', lineHeight: '1.4' }}>
        {billFormat?.footer_1 && <div>{billFormat.footer_1}</div>}
        {billFormat?.footer_2 && <div>{billFormat.footer_2}</div>}
      </div>
    </div>
  );
};
export default ThreeInchInvoice;
